import { useState } from 'react';

import { ChevronsUpDown, Building2, Settings } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { useGetGames } from '@/api/getGames';
import { useMyPermissions } from '@/api/getMyPermissions';
import { useGetStudios } from '@/api/getStudios';
import { StudioLogo } from '@/components/studioLogo';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Text } from '@/components/ui/typography';
import { managesInStudio } from '@/models/permission';

/**
 * The current Studio, always shown, with a switch to every Studio the viewer can
 * reach. Picking one opens its dashboard when the viewer administers it, else its
 * first reachable Game — a non-administrator has no studio page to land on.
 */
export const StudioBreadcrumb = ({ studioCode }: { studioCode: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { data: studiosData } = useGetStudios();
  const { data: gamesData } = useGetGames();
  const { data: me } = useMyPermissions();

  const roles = me?.roles ?? [];
  const studios = studiosData?.studios ?? [];
  const name =
    studios.find(({ code }) => code === studioCode)?.name ?? studioCode;
  const administersCurrent = managesInStudio(roles, studioCode);

  const landingOf = (code: string) => {
    if (managesInStudio(roles, code)) return `/studios/${code}`;
    const game = gamesData?.games.find(
      ({ studio_code }) => studio_code === code,
    );
    return game ? `/studios/${code}/${game.game_code}` : '/studios';
  };

  const switchStudio = (code: string) => {
    setIsOpen(false);
    if (code !== studioCode) navigate(landingOf(code));
  };

  const content = (
    <>
      <Text variant="caption">Studio</Text>
      <StudioLogo code={studioCode} className="h-5 w-8" />
      <span className="truncate max-w-48 text-foreground">{name}</span>
    </>
  );

  // Nothing to switch to, and no studio page to list them from either.
  if (studios.length < 2 && !administersCurrent)
    return (
      <span className="flex h-7 min-w-0 items-center gap-1.5 rounded-md border px-2 text-sm">
        {content}
      </span>
    );

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      {/* Same labelled whole-crumb trigger as the Game and Sandbox crumbs. */}
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 min-w-0 gap-1.5 px-2"
          title="Switch studio"
        >
          {content}
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-xs">
        <DropdownMenuRadioGroup
          value={studioCode}
          onValueChange={switchStudio}
          className="max-h-48 overflow-y-auto"
        >
          {studios.map((studio) => (
            <DropdownMenuRadioItem key={studio.code} value={studio.code}>
              <span className="truncate">{studio.name}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/studios">
            <Building2 className="size-4" />
            <span>All studios</span>
          </Link>
        </DropdownMenuItem>
        {administersCurrent && (
          <DropdownMenuItem asChild>
            <Link to={`/studios/${studioCode}`}>
              <Settings className="size-4" />
              <span>Manage studio</span>
            </Link>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
